'use client'

import moment from 'moment'
import { Line, LineChart, XAxis, YAxis } from 'recharts'

import { CurrencyAmount } from '~/components/component-library/CurrencyAmount'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~/components/ui/chart'
import { displayCurrency } from '~/utils/display'

import type { PriceChartPeriod, PricePoint } from '~/hooks/useTokenPriceChart'

export const CHART_HEIGHT_PX = 250

const TICK_FONT_SIZE_PX = 12
const LABEL_BACKGROUND_PAD_PX = 2
// Keeps the mirrored labels inside the plot: the bottom tick would otherwise
// sit on the plot edge and spill into the X-axis labels.
const Y_AXIS_PADDING = { top: 4, bottom: 12 }

// ChartContainer forces every axis tick to fill-muted-foreground via a CSS
// rule, which beats the inline `fill` attribute. An inline style wins.
const TICK_STYLE = { fill: 'var(--color-secondary)' }

const measureTextWidth = (text: string) => {
  if (typeof document === 'undefined') return 0
  const context = document.createElement('canvas').getContext('2d')
  if (!context) return 0
  context.font = `${TICK_FONT_SIZE_PX}px ${getComputedStyle(document.body).fontFamily}`
  return context.measureText(text).width
}

// Price labels sit on top of the line, so each gets a translucent backdrop in
// the dialog's surface color to keep it legible where the two overlap.
const PriceTick = ({
  x,
  y,
  label,
}: {
  x: number
  y: number
  label: string
}) => {
  const height = TICK_FONT_SIZE_PX + LABEL_BACKGROUND_PAD_PX * 2
  return (
    <g>
      <rect
        x={x}
        y={y - height / 2}
        width={measureTextWidth(label) + LABEL_BACKGROUND_PAD_PX * 2}
        height={height}
        rx={2}
        className="fill-white dark:fill-black"
        fillOpacity={0.5}
      />
      <text
        x={x}
        y={y}
        textAnchor="start"
        dominantBaseline="central"
        fontSize={TICK_FONT_SIZE_PX}
        style={TICK_STYLE}
        className="recharts-cartesian-axis-tick-value"
      >
        {label}
      </text>
    </g>
  )
}

type TickRenderProps = { x: number; y: number; payload: { value: number } }

const TICK_FORMATS: Record<PriceChartPeriod, string> = {
  '24h': 'HH:mm',
  '7d': 'MMM D',
  '30d': 'MMM D',
  '1y': 'MMM YYYY',
}

const TOOLTIP_FORMATS: Record<PriceChartPeriod, string> = {
  '24h': 'MMM D, HH:mm',
  '7d': 'MMM D, HH:mm',
  '30d': 'MMM D, YYYY',
  '1y': 'MMM D, YYYY',
}

export const PriceChart = ({
  prices,
  period,
}: {
  prices: PricePoint[]
  period: PriceChartPeriod
}) => {
  const tickFormat = TICK_FORMATS[period]
  const tooltipFormat = TOOLTIP_FORMATS[period]
  const values = prices.map(({ p }) => p)
  const priceRange = Math.max(...values) - Math.min(...values)
  const decimalPoints =
    priceRange > 0
      ? Math.max(2, Math.min(12, Math.ceil(-Math.log10(priceRange / 4))))
      : 2
  const formatPriceTick = (value: number) =>
    displayCurrency(value, { decimalPoints })

  return (
    <ChartContainer
      config={{ p: { label: 'Price' } }}
      className="w-full text-blue-500"
      style={{ height: CHART_HEIGHT_PX }}
    >
      <LineChart
        data={prices}
        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
      >
        <XAxis
          dataKey="t"
          type="number"
          domain={['dataMin', 'dataMax']}
          scale="time"
          tickFormatter={(value: number) => moment(value).format(tickFormat)}
          minTickGap={48}
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: TICK_FONT_SIZE_PX, style: TICK_STYLE }}
          stroke="currentColor"
          className="text-secondary"
        />
        <YAxis
          domain={['auto', 'auto']}
          padding={Y_AXIS_PADDING}
          mirror
          width="auto"
          tickCount={5}
          tickFormatter={formatPriceTick}
          tickLine={false}
          axisLine={false}
          // Zero both so labels start flush with the plot's left edge.
          tickSize={0}
          tickMargin={0}
          tick={(props: unknown) => {
            const { x, y, payload } = props as TickRenderProps
            return (
              <PriceTick x={x} y={y} label={formatPriceTick(payload.value)} />
            )
          }}
          stroke="currentColor"
          className="text-secondary"
        />
        <ChartTooltip
          cursor={{ stroke: 'currentColor', strokeOpacity: 0.2 }}
          content={
            <ChartTooltipContent
              className="text-primary"
              labelFormatter={(_, payload) => {
                const t = payload?.[0]?.payload?.t as number | undefined
                return t ? moment(t).format(tooltipFormat) : ''
              }}
              formatter={(value) => (
                <CurrencyAmount layout="inline" value={value as number} />
              )}
              hideIndicator
            />
          }
        />
        <Line
          dataKey="p"
          type="monotone"
          stroke="currentColor"
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  )
}
